import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from 'tea-component';
import { useOutsideClick } from 'tea-component/es/util/outsideclick/useOutsideClick';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import TextEdit from './TextEdit';
import TextBackgroundColorSetting from './TextBackgroundColorSetting';
import { IColorInfo } from '@/pages/datalab/components/ColorPalette';

import '../index.scss';
import { useTokenFailureSetSessionCache } from '@/pages/datalab/pages/Multidimensional/Analysis';

interface ITextLayoutItem {
  i: string;
  text: string;
  backgroundColor: IColorInfo;
}

interface ITextConfirmValue {
  type: 'text';
  text: string;
  backgroundColor: IColorInfo;
}

interface ITextProps {
  leftLayoutSelectItem: ITextLayoutItem | null;
  onConfirm: (value: ITextConfirmValue, isHidden: boolean) => void;
  onClose: () => void;
}

export default function Text(porps: ITextProps) {
  const { leftLayoutSelectItem, onConfirm, onClose } = porps;
  const { dblFormatMessage, commonFormatMessage, isEn } = useDblIntl('create_dashboard');
  const [content, setContent] = useState('');
  const [backgroundColor, setBackgroundColor] = useState<IColorInfo>({ hexa: '#ffffffff', alpha: '100' });
  const containerRef = useRef<HTMLDivElement>(null);

  // 用 ref 保存最新值，避免 useEffect 闭包引用旧值或频繁重绑事件
  const contentRef = useRef(content);
  contentRef.current = content;
  const bgColorRef = useRef(backgroundColor);
  bgColorRef.current = backgroundColor;
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  // 点击组件外部时自动保存（等同于点击"保存"按钮，但右侧设置面板不隐藏）
  // 方案演进说明：
  // 1. onBlur：颜色弹层(Popover)关闭时会打断焦点事件链，导致 onBlur 不触发 ❌
  // 2. click 事件：Popover 在 mousedown 阶段关闭并触发 onChange，到 click 阶段 ref 已是最新值，
  //    但点击 Dashboard 卡片区域会同时触发保存和切换卡片，导致数据冲突 ❌
  // 3. useOutsideClick + setTimeout：延迟到下一个宏任务确保 onChange 先执行，
  //    但用户点击左侧卡片时 leftLayoutSelectItem.i 已变化，保存到错误卡片 ❌
  // 4. useOutsideClick（当前）：mousedown 触发，简单直接，由父组件保证 leftLayoutSelectItem 稳定 ✅
  useOutsideClick(containerRef).listen(() => {
    onConfirmRef.current(
      { type: 'text', text: contentRef.current, backgroundColor: bgColorRef.current },
      false,
    );
  });

  // 工具栏配置
  const toolbarConfig = {
    toolbarKeys: [
      'fontFamily',
      'fontSize',
      'bold',
      'underline',
      'italic',
      'color',
    ],
  };
  const { userFailureValue, takeEffect } = useTokenFailureSetSessionCache(
    useMemo(() => ({ content, backgroundColor }), [content, backgroundColor]),
  );
 
  useEffect(() => {
    if (leftLayoutSelectItem) {
      const { text, backgroundColor: bgColor } = leftLayoutSelectItem;
      setContent(text);
      if (bgColor?.hexa) {
        setBackgroundColor(bgColor);
      } else {
        setBackgroundColor({ hexa: '#ffffffff', alpha: '100' });
      }
    }
  }, [leftLayoutSelectItem]);

  useEffect(() => {
    if (takeEffect) {
      const { content, backgroundColor } = userFailureValue || {};
      content && setContent(content);
      backgroundColor && setBackgroundColor(backgroundColor);
    }
  }, [takeEffect, userFailureValue]);

  return (
    <div
      className={`dbl-exit-modal-show`}
      ref={containerRef}
    >
      <div className="dbl-exit-modal-title">{dblFormatMessage('text')}</div>
      <div className="dbl-exit-modal-context">
        <div className="dbl-exit-text">
          <TextEdit
            customStyle={{
              minHeight: 250,
            }}
            toolbarConfig={toolbarConfig}
            maxContentHeight={190}
            maxLength={12800}
            isEn={isEn}
            key={isEn ? 'en' : 'zh-CN'}
            defaultValue={content}
            placeholder={dblFormatMessage('text_placeholder')}
            autoFocus={false}
            getEditorContent={(v: string) => {
              setContent(v);
            }}
          />
        </div>
        <TextBackgroundColorSetting
          value={backgroundColor}
          onChange={setBackgroundColor}
          popupContainer={() => containerRef.current!}
        />

      </div>
      <div className='tea-dialog__btnwrap'>
        <Button
          type="primary"
          onClick={() => {
            onConfirm({ type: 'text', text: content, backgroundColor }, true);
          }}
        >
          {commonFormatMessage("save")}
        </Button>
        <Button type="weak" htmlType="button" onClick={() => onClose()}>
          {commonFormatMessage("back")}
        </Button>
      </div>
    </div>
  );
}
